import { useState, useCallback } from "../imports";
import { useClientPrincipal } from "../context/ClientPrincipalContext";
import { useNotification } from "../context/NotificationContext";
import { useConfirm } from "../providers/ConfirmProvider";

export interface WifiData {
  id: string;
  name: string;
  ssid: string;
  encryptionType: string;
  qrString?: string;
}

export interface WifiCreateDto {
  name: string;
  ssid: string;
  password: string;
  encryptionType: "WPA2" | "WPA3";
}

const useWifiActions = () => {
  const [wifiList, setWifiList] = useState<Record<string, WifiData>>({});
  const [error, setError] = useState<string>("");
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { clientPrincipal } = useClientPrincipal();
  const { setNotification } = useNotification();
  const confirm = useConfirm();

  const fetchWifiList = useCallback(
    async (userIdOverride?: string) => {
      const userId = userIdOverride || clientPrincipal?.userId || "";
      if (!userId) return;

      try {
        const response = await fetch(`${backendUrl}/wifi`, {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "client-principal-user-id": userId,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch WiFi list");

        const baseData: WifiData[] = await response.json();

        const detailedEntries: WifiData[] = await Promise.all(
          baseData.map(async (wifi) => {
            try {
              const detailRes = await fetch(`${backendUrl}/wifi/${wifi.id}`, {
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                  "client-principal-user-id": userId,
                },
              });
              if (detailRes.ok) {
                const detail = await detailRes.json();
                return { ...wifi, ...detail };
              }
              return wifi;
            } catch {
              return wifi;
            }
          })
        );

        const entries: [string, WifiData][] = detailedEntries.map((wifi) => [
          wifi.id,
          wifi,
        ]);
        setWifiList(Object.fromEntries(entries));
      } catch (err) {
        setError("Failed to fetch WiFi list");
        setNotification({
          type: "error",
          message: "Failed to load WiFi list. Please try again later.",
        });
      }
    },
    [backendUrl, clientPrincipal, setNotification]
  );

  const fetchWifiDetails = useCallback(
    async (id: string, userIdOverride?: string) => {
      const userId = userIdOverride || clientPrincipal?.userId;
      if (!userId) return null;

      try {
        const response = await fetch(`${backendUrl}/wifi/${id}`, {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "client-principal-user-id": userId,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch WiFi details");
        return (await response.json()) as WifiData;
      } catch (err) {
        setError("Failed to fetch WiFi details");
        setNotification({
          type: "error",
          message: "Failed to load WiFi details. Please try again later.",
        });
        return null;
      }
    },
    [backendUrl, clientPrincipal, setNotification]
  );

  const createWifi = useCallback(
    async (wifi: WifiCreateDto, userIdOverride?: string) => {
      const userId = userIdOverride || clientPrincipal?.userId;
      if (!userId) return null;

      try {
        const response = await fetch(`${backendUrl}/wifi`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "client-principal-user-id": userId,
          },
          body: JSON.stringify(wifi),
        });

        if (!response.ok) throw new Error("Failed to create WiFi");
        const created = (await response.json()) as WifiData;

        const detailed = await fetchWifiDetails(created.id, userId);

        setWifiList((prev) => ({
          ...prev,
          [created.id]: detailed || created,
        }));

        setNotification({
          type: "success",
          message: "WiFi QR Code created successfully!",
        });

        return detailed || created;
      } catch (err) {
        setError("Failed to create WiFi");
        setNotification({
          type: "error",
          message: "Failed to create WiFi. Please try again later.",
        });
        return null;
      }
    },
    [backendUrl, clientPrincipal, setNotification, fetchWifiDetails]
  );

  const deleteWifi = useCallback(
    async (id: string, name: string, userIdOverride?: string) => {
      const confirmed = await confirm({
        title: "Delete WiFi QR Code",
        message: `Are you sure you want to delete "${name}"?`,
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
      });

      if (!confirmed) return false;

      const userId = userIdOverride || clientPrincipal?.userId;
      if (!userId) return false;

      try {
        const response = await fetch(`${backendUrl}/wifi/${id}`, {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "client-principal-user-id": userId,
          },
        });

        if (!response.ok) throw new Error("Failed to delete WiFi");

        setWifiList((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });

        setNotification({
          type: "success",
          message: "WiFi QR Code deleted successfully!",
        });

        return true;
      } catch (err) {
        setError("Failed to delete WiFi");
        setNotification({
          type: "error",
          message: "Failed to delete WiFi. Please try again later.",
        });
        return false;
      }
    },
    [backendUrl, clientPrincipal, setNotification, confirm]
  );

  return {
    wifiList,
    error,
    fetchWifiList,
    fetchWifiDetails,
    createWifi,
    deleteWifi,
    setWifiList,
  };
};

export default useWifiActions;
